import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import Label from "../../Utils/FormInputs/Label";
import TextField from "../../Utils/FormInputs/TextField";
import { RightAlignedContainer } from "../../Utils/styledComponents";
import Textarea from "../../Utils/FormInputs/Textarea";
import SelectInput, { CustomSelect } from "../../Utils/FormInputs/SelectInput";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import InputGroup from "react-bootstrap/InputGroup";
import axios from "axios";
import { config } from "../../../Helpers/env";
import { errorMessages, logoutErrorMessage } from "../../../Helpers/ErrorMessages";
import { AccountingDrawer } from "../../Utils/AccountingUtils/AccountingDrawer";
import { dateConvert } from "../../Utils/ConvertDate";
import { useCurrency } from "../../../Context/currency-context";
import ProductDisptachList from "../../Inventory/stock/ProductDisptachList";

export default function ReceiveSinglePayment({ actions, openDialog, setOpenDialog, isSubmitted, setIsSubmitted, providerDetails }) {
  const { currency: baseCurrency } = useCurrency({ actions });
  const user = JSON.parse(sessionStorage.getItem("user")) || {};
  const token = user.token?.[1] || {};
  const user_roles_id = token.user_roles_id || "";

  const [state, setState] = useState({ payment_date: dateConvert() });
  const [submitted, setSubmitted] = useState(false);
  const [memo, setMemo] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [debitTo, setDebitTo] = useState("");
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [displayRate, setDisplayRate] = useState(1400); // User sees SSP → USD
  const [decimalRate, setDecimalRate] = useState(1 / 1400); // Internal API rate
  const [amountReceived, setAmountReceived] = useState(0);
  const [isFull, setIsFull] = useState(false);
  const [totalBill, setTotalBill] = useState(0);

  // ===== Fetch currencies =====
  useEffect(() => {
    axios
      .get(`${config.smsUrl}/accounting/currencies`)
      .then((res) => {
        const arr = res.data?.data || [];
        setCurrencies(
          arr.map((item) => ({
            id: item.id,
            value: `${item.id}-${item.is_base_currency}-${item.currency_symbol}`,
            text: `${item.currency_symbol} ${item.currency_name}`,
            isBase: item.is_base_currency,
            symbol: item.currency_symbol,
            exchange_rate: item.exchange_rate || 1,
          }))
        );
      })
      .catch((err) => logoutErrorMessage(err, null, actions));
  }, []);

  // ===== Fetch accounts =====
  useEffect(() => {
    axios
      .get(`${config.smsUrl}/accounting/ledgers_under_category`)
      .then((res) => {
        const arr = res.data?.data || [];
        setAccounts(arr.map((item) => ({ value: item.id, label: item.head_name })));
      })
      .catch((err) => errorMessages(err, null, actions));
  }, []);

  // ===== Initialize with provider data =====
//   useEffect(() => {
//     if (!providerDetails) return;

//     const { grand_total, currency_id, currency_symbol, is_base_currency } = providerDetails;
//     setTotalBill(grand_total);
//     setSelectedCurrency(`${currency_id}-${is_base_currency}-${currency_symbol}`);

//     if (is_base_currency) {
//       setDecimalRate(1);
//       setDisplayRate(1);
//     } else {
//       setDecimalRate(1 / 1400); // internal rate
//       setDisplayRate(1400); // show 1400 SSP → 1 USD
//     }

//     setAmountReceived(0);
//     setIsFull(false);
//     setDebitTo("");
//     setMemo("");
//   }, [providerDetails]);
// ===== Initialize with provider data =====
useEffect(() => {
    if (!providerDetails) return;
  
    const { grand_total, currency_id, currency_symbol, is_base_currency } = providerDetails;
  
    // Set the selected currency
    setSelectedCurrency(`${currency_id}-${is_base_currency}-${currency_symbol}`);
  
    if (is_base_currency) {
      setDecimalRate(1);
      setDisplayRate(1);
      setTotalBill(grand_total); // Base currency, no conversion
    } else {
      setDisplayRate(1400); // default SSP → USD
      setDecimalRate(1 / 1400);
      setTotalBill(grand_total / 1400); // Convert to base currency by dividing
    }
  
    setAmountReceived(0);
    setIsFull(false);
    setDebitTo("");
    setMemo("");
  }, [providerDetails]);
  
  // ===== Handlers =====
  const handleChangeCurrency = (e) => {
    const selected = e.target.value;
    setSelectedCurrency(selected);

    const [currencyId, isBase] = selected.split("-");
    if (isBase === "true") {
      setDecimalRate(1);
      setDisplayRate(1);
    } else {
      setDecimalRate(1 / 1400);
      setDisplayRate(1400);
    }

    setAmountReceived(0);
    setIsFull(false);
  };

//   const handleChangeDisplayRate = (e) => {
//     const userRate = +e.target.value > 0 ? +e.target.value : 1400;
//     setDisplayRate(userRate);
//     setDecimalRate(1 / userRate);
//   };
const handleChangeDisplayRate = (e) => {
    const userRate = e.target.value; // keep as string
    setDisplayRate(userRate);
  
    // Only compute decimalRate if user entered a valid number
    if (userRate && +userRate > 0) {
      setDecimalRate(1 / +userRate);
    } else {
      setDecimalRate(0); // no valid rate
    }
  };
  

  
  const handleChangeReceived = (e) => setAmountReceived(+e.target.value);

  const handleIsFullBill = (e) => {
    const checked = e.target.checked;
    setIsFull(checked);

    if (!checked) {
      setAmountReceived(0);
      return;
    }

    const isBaseCurrency = selectedCurrency.split("-")[1] === "true";
    const amount = isBaseCurrency ? totalBill : totalBill * displayRate;
    setAmountReceived(amount);
  };

  // ===== Submit Payment =====
  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (!providerDetails || !debitTo || !amountReceived) return;

    const [currency_id, isBase] = selectedCurrency.split("-");
    const isBaseCurrency = isBase === "true";
    const amountInBase = isBaseCurrency ? amountReceived : amountReceived * decimalRate;

    const params = {
      visit_id: providerDetails.visit_id,
      amount_paid: amountInBase,
      pay_type: 2,
      received_by: user_roles_id,
      memo,
      insurance_provider: +providerDetails?.provider_id,
      debit_to: debitTo?.value,
      currency_id: Number(currency_id),
      currency_rate: decimalRate,
      payment_date: state.payment_date,
    };

    setIsSubmitted("pending");
    axios
      .post(`${config.smsUrl}/cbilling/clear_debt`, params)
      .then(() => {
        actions.snackbarActions.snackSuccess("Payment recorded successfully");
        setIsSubmitted("resolved");
        setSubmitted(false);
        setOpenDialog(false);
      })
      .catch((err) => {
        errorMessages(err, null, actions);
        setIsSubmitted("rejected");
      });
  };

  const isSelectedBaseCurrency = selectedCurrency.split("-")[1] === "true";
//   console.log(isSelectedBaseCurrency)
//   console.log(providerDetails)
//   const convertedTotal = isSelectedBaseCurrency ? totalBill : totalBill * displayRate;
  // Computed converted total
  const convertedTotal = isSelectedBaseCurrency
    ? totalBill
    : displayRate && +displayRate > 0
      ? totalBill * +displayRate
      : 0;

      const isReadOnlyRate = !providerDetails.is_base_currency;
      console.log(isReadOnlyRate)
    //   alert(providerDetails?.isBaseCurrency)

  return (
    <AccountingDrawer
      open={openDialog}
      title={`Payment for ${providerDetails?.provider_name}`}
      handleClose={() => setOpenDialog(false)}
      width={800}
    >
      <form onSubmit={handleSubmit}>
        <div className="row mb-3">
          <div className="col-lg-8">
            {/* Payment Date */}
            <Form.Group>
              <Label name="Payment Date" />
              <TextField
                type="date"
                value={state.payment_date}
                name="payment_date"
                submitted={submitted}
                onChange={(e) => setState({ ...state, payment_date: e.target.value })}
              />
            </Form.Group>

            {/* Currency */}
            <Form.Group>
              <Label name="Currency" />
              <SelectInput
                value={selectedCurrency}
                onChange={handleChangeCurrency}
                options={currencies}
                defaultOption="Select..."
              />
            </Form.Group>

            {/* Exchange Rate */}
            {!isSelectedBaseCurrency && (
              <Form.Group>
                <Label name="Exchange Rate" />
                <InputGroup>
                  <InputGroup.Text>{selectedCurrency.split("-")[2]}</InputGroup.Text>
                  <TextField
                    type="number"
                    value={displayRate}
                    onChange={handleChangeDisplayRate}
                    readOnly={isReadOnlyRate} // <-- Make it read-only
                    />
                </InputGroup>
              </Form.Group>
            )}

            {/* Total Bill */}
            <Form.Group>
              <Label name="Total Bill" />
              <Row>
                <Col>
                  <InputGroup>
                    <InputGroup.Text>{baseCurrency.currency_symbol}</InputGroup.Text>
                    <TextField value={totalBill} readOnly />
                  </InputGroup>
                </Col>
                {!isSelectedBaseCurrency && (
                  <Col>
                    <InputGroup>
                      <InputGroup.Text>{selectedCurrency.split("-")[2]}</InputGroup.Text>
                      <TextField value={convertedTotal} readOnly />
                    </InputGroup>
                  </Col>
                )}
              </Row>
            </Form.Group>

            {/* Amount Received */}
            <Form.Group>
              <Label name="Amount Received" />
              <TextField
                type="number"
                value={amountReceived}
                submitted={submitted}
                onChange={handleChangeReceived}
              />
              <Form.Check
                checked={isFull}
                onChange={handleIsFullBill}
                label={`Receive full amount (${isSelectedBaseCurrency ? totalBill : convertedTotal})`}
              />
            </Form.Group>

            {/* Debit To */}
            <Form.Group>
              <Label name="Debit To" />
              <CustomSelect
                value={debitTo}
                onChange={setDebitTo}
                options={accounts}
                submitted={submitted}
              />
            </Form.Group>

            {/* Memo */}
            <Form.Group>
            <Label name="Memo" />
              <Textarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={3} />
            </Form.Group>

            {/* Submit Button */}
            <RightAlignedContainer>
              <button
                type="submit"
                className="btn btn-sm sms-btn"
                disabled={isSubmitted === "pending"}
              >
                {isSubmitted === "pending" ? "Submitting..." : "Submit"}
              </button>
            </RightAlignedContainer>
          </div>
        </div>
      </form>
    </AccountingDrawer>
  );
}
             
